import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  FileText, 
  Users, 
  AlertTriangle
} from 'lucide-react';
import { TakaIcon } from '../../components/UI';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { formatDateTime } from '../../utils/formatDateTime';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const currencySymbol = settings?.invoice?.defaultCurrencySymbol || '৳';

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await apiService.get('/reports/dashboard-overview');
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch data if user is authenticated
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    change?: string;
  }> = ({ title, value, icon, color, change }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 } as any}
      animate={{ opacity: 1, y: 0 } as any}
      className={`bg-[var(--color-surface)] rounded-lg shadow-lg p-6 border-l-4 ${color} font-[var(--font-family)] text-[var(--color-text)]`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-[var(--color-text-secondary)] font-[var(--font-family)]">{title}</p>
          <p className="text-2xl font-bold text-[var(--color-text)] font-[var(--font-family)]">{value}</p>
          {change && (
            <p className="text-sm text-[var(--color-success)] font-[var(--font-family)]">{change}</p>
          )}
        </div>
        <div className="p-3 bg-[var(--color-primary-light)] rounded-full">
          {icon}
        </div>
      </div>
    </motion.div>
  );

  const RecentItem: React.FC<{
    title: string;
    subtitle: string;
    amount?: string;
    date: string;
    status?: string;
  }> = ({ title, subtitle, amount, date, status }) => (
    <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)] last:border-b-0 font-[var(--font-family)] text-[var(--color-text)]">
      <div className="flex-1">
        <h4 className="font-medium">{title}</h4>
        <p className="text-sm text-[var(--color-text-secondary)]">{subtitle}</p>
        <p className="text-xs text-[var(--color-text-secondary)]">{date}</p>
      </div>
      {amount && (
        <div className="text-right">
          <p className="font-medium">{amount}</p>
          {status && (
            <span className={`inline-block px-2 py-1 text-xs rounded-full ${
              status === 'completed' ? 'bg-[var(--color-success-light)] text-[var(--color-success)]' :
              status === 'pending' ? 'bg-[var(--color-warning-light)] text-[var(--color-warning)]' :
              'bg-[var(--color-error-light)] text-[var(--color-error)]'
            }`}>
              {status}
            </span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6 font-[var(--font-family)] text-[var(--color-text)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            Welcome back, {user?.firstName || 'User'}!
          </h1>
          <p className="text-[var(--color-text-secondary)] font-[var(--font-family)]">
            Here's what's happening with your inventory today.
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-[var(--color-text-secondary)] font-[var(--font-family)]">Current Date</p>
          <p className="text-lg font-semibold text-[var(--color-text)] font-[var(--font-family)]">
            {formatDateTime(new Date(), settings)}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 } as any}
        animate={{ opacity: 1, y: 0 } as any}
        className="bg-[var(--color-surface)] rounded-lg shadow-lg p-6"
      >
        <h2 className="text-xl font-semibold text-[var(--color-text)] font-[var(--font-family)]">
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button 
            className="flex flex-col items-center p-4 bg-[var(--color-primary-light)] rounded-lg hover:bg-[var(--color-primary-lighter)] transition-colors"
            onClick={() => navigate('/products')}
          >
            <Package className="w-8 h-8 text-[var(--color-primary)] mb-2" />
            <span className="text-sm font-medium text-[var(--color-text)] font-[var(--font-family)]">Add Product</span>
          </button>
          <button 
            className="flex flex-col items-center p-4 bg-[var(--color-success-light)] rounded-lg hover:bg-[var(--color-success-lighter)] transition-colors"
            onClick={() => navigate('/invoices/purchase')}
          >
            <FileText className="w-8 h-8 text-[var(--color-success)] mb-2" />
            <span className="text-sm font-medium text-[var(--color-text)] font-[var(--font-family)]">Create Purchase Invoice</span>
          </button>
          <button 
            className="flex flex-col items-center p-4 bg-[var(--color-primary-light)] rounded-lg hover:bg-[var(--color-primary-lighter)] transition-colors"
            onClick={() => navigate('/invoices/delivery')}
          >
            <FileText className="w-8 h-8 text-[var(--color-primary)] mb-2" />
            <span className="text-sm font-medium text-[var(--color-text)] font-[var(--font-family)]">Create Delivery Invoice</span>
          </button>
          <button 
            className="flex flex-col items-center p-4 bg-[var(--color-warning-light)] rounded-lg hover:bg-[var(--color-warning-lighter)] transition-colors"
            onClick={() => navigate('/invoices/return')}
          >
            <FileText className="w-8 h-8 text-[var(--color-warning)] mb-2" />
            <span className="text-sm font-medium text-[var(--color-text)] font-[var(--font-family)]">Create Return Invoice</span>
          </button>
          <button 
            className="flex flex-col items-center p-4 bg-[var(--color-secondary-light)] rounded-lg hover:bg-[var(--color-secondary-lighter)] transition-colors"
            onClick={() => navigate('/customers')}
          >
            <Users className="w-8 h-8 text-[var(--color-secondary)] mb-2" />
            <span className="text-sm font-medium text-[var(--color-text)] font-[var(--font-family)]">New Customer</span>
          </button>
          <button 
            className="flex flex-col items-center p-4 bg-[var(--color-secondary-light)] rounded-lg hover:bg-[var(--color-secondary-lighter)] transition-colors"
            onClick={() => navigate('/projects')}
          >
            <Users className="w-8 h-8 text-[var(--color-secondary)] mb-2" />
            <span className="text-sm font-medium text-[var(--color-text)] font-[var(--font-family)]">New Project</span>
          </button>
          <button 
            className="flex flex-col items-center p-4 bg-[var(--color-primary-light)] rounded-lg hover:bg-[var(--color-primary-lighter)] transition-colors"
            onClick={() => navigate('/suppliers')}
          >
            <Package className="w-8 h-8 text-[var(--color-primary)] mb-2" />
            <span className="text-sm font-medium text-[var(--color-text)] font-[var(--font-family)]">New Supplier</span>
          </button>
          <button 
            className="flex flex-col items-center p-4 bg-[var(--color-warning-light)] rounded-lg hover:bg-[var(--color-warning-lighter)] transition-colors"
            onClick={() => navigate('/reports')}
          >
            <Package className="w-8 h-8 text-[var(--color-warning)] mb-2" />
            <span className="text-sm font-medium text-[var(--color-text)] font-[var(--font-family)]">View Reports</span>
          </button>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Products"
          value={stats?.products?.total || 0}
          icon={<Package className="w-6 h-6 text-[var(--color-primary)]" />}
          color="border-[var(--color-primary)]"
          change="+12% from last month"
        />
        <StatCard
          title="Total Invoices"
          value={stats?.invoices?.total || 0}
          icon={<FileText className="w-6 h-6 text-[var(--color-success)]" />}
          color="border-[var(--color-success)]"
          change="+8% from last month"
        />
        <StatCard
          title="Active Projects"
          value={stats?.projects?.active || 0}
          icon={<Users className="w-6 h-6 text-[var(--color-secondary)]" />}
          color="border-[var(--color-secondary)]"
        />
        <StatCard
          title="Total Revenue"
          value={`${currencySymbol}${(stats?.financial?.totalAmount || 0).toLocaleString()}`}
          icon={<TakaIcon className="w-8 h-8 text-[var(--color-warning)]" />}
          color="border-[var(--color-warning)]"
          change="+15% from last month"
        />
      </div>

      {/* Alerts */}
      {stats?.products?.lowStock && stats.products.lowStock > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 } as any}
          animate={{ opacity: 1, y: 0 } as any}
          className="bg-[var(--color-warning-light)] border border-[var(--color-warning)] rounded-lg p-4"
        >
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-[var(--color-warning)] mr-3" />
            <div>
              <h3 className="font-medium text-[var(--color-warning)] font-[var(--font-family)]">
                Low Stock Alert
              </h3>
              <p className="text-sm text-[var(--color-warning)] font-[var(--font-family)]">
                {stats.products.lowStock} products are running low on stock.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <motion.div
          initial={{ opacity: 0, x: -20 } as any}
          animate={{ opacity: 1, x: 0 } as any}
          className="bg-[var(--color-surface)] rounded-lg shadow-lg"
        >
          <div className="p-6 border-b border-[var(--color-border)]">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[var(--color-text)] font-[var(--font-family)]">
                Recent Invoices
              </h2>
              <button 
                className="text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] text-sm font-medium"
                onClick={() => navigate('/invoices')}
              >
                View All
              </button>
            </div>
          </div>
          <div className="divide-y divide-[var(--color-border)]">
            {stats?.recentActivity?.invoices?.slice(0, 5).map((invoice: any, index: number) => (
              <RecentItem
                key={invoice._id}
                title={`Invoice #${invoice.invoiceNumber}`}
                subtitle={invoice.type || 'N/A'}
                amount={`${currencySymbol}${invoice.total?.toLocaleString()}`}
                date={formatDateTime(invoice.createdAt, settings)}
                status={invoice.status}
              />
            ))}
            {(!stats?.recentActivity?.invoices || stats.recentActivity.invoices.length === 0) && (
              <div className="p-6 text-center text-[var(--color-text-secondary)] font-[var(--font-family)]">
                No recent invoices
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Products */}
        <motion.div
          initial={{ opacity: 0, x: 20 } as any}
          animate={{ opacity: 1, x: 0 } as any}
          className="bg-[var(--color-surface)] rounded-lg shadow-lg"
        >
          <div className="p-6 border-b border-[var(--color-border)]">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[var(--color-text)] font-[var(--font-family)]">
                Recent Products
              </h2>
              <button 
                className="text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] text-sm font-medium"
                onClick={() => navigate('/products')}
              >
                View All
              </button>
            </div>
          </div>
          <div className="divide-y divide-[var(--color-border)]">
            {stats?.recentActivity?.products?.slice(0, 5).map((product: any, index: number) => (
              <RecentItem
                key={product._id}
                title={product.name}
                subtitle={product.category?.name || 'N/A'}
                amount={`${currencySymbol}${product.price?.toLocaleString()}`}
                date={formatDateTime(product.createdAt, settings)}
              />
            ))}
            {(!stats?.recentActivity?.products || stats.recentActivity.products.length === 0) && (
              <div className="p-6 text-center text-[var(--color-text-secondary)] font-[var(--font-family)]">
                No recent products
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Top Categories */}
      {stats?.topCategories && stats.topCategories.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 } as any}
          animate={{ opacity: 1, y: 0 } as any}
          className="bg-[var(--color-surface)] rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-semibold text-[var(--color-text)] font-[var(--font-family)]">
            Top Categories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stats.topCategories.slice(0, 6).map((category: any, index: number) => (
              <div key={category._id} className="flex items-center justify-between p-3 bg-[var(--color-surface-light)] rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-[var(--color-primary)] rounded-full mr-3"></div>
                  <span className="font-medium text-[var(--color-text)] font-[var(--font-family)]">
                    {category.name}
                  </span>
                </div>
                <span className="text-sm text-[var(--color-text-secondary)] font-[var(--font-family)]">
                  {category.productCount} products
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

    </div>
  );
};

export default DashboardPage; 